<script setup>
import { computed, onMounted, reactive, watch } from 'vue'
import { useRoute } from 'vue-router';
import { useProductsStore } from '@/stores/products';
import Accordion from '@/components/accordion/AccordionComponent.vue';
import AccordionGroup from '@/components/accordion/AccordionGroup.vue';
import ProductAttribute from "@/components/ProductAttribute.vue";
import { getImageUrl } from "@/utils/getImageUrl";
import { getImageSize } from "@/utils/getImageSize";

const productsStore = useProductsStore();
const route = useRoute();
const imageSizes = reactive({}); // To store image sizes

const currentProduct = computed(() => {
  return productsStore.initialProducts.find((el) => el.url === `/${route.params.company}/${route.params.id}`)
})

const images = computed(() => {
  return currentProduct?.value?.images || []
})

const section = computed(() => {
  return productsStore.section || {}
})

const localCurrent = reactive({ index: 0, tabs: {} });
const skinSettings = reactive({ useFotorama: false })


function getImageSizeForImage(image) {
  // console.log({ image })
  return imageSizes[image.name] || { width: 0, height: 0 };

}

onMounted(() => {
  setCurrentIndex()
})

function setCurrentIndex() {
  const idx = productsStore.initialProducts.findIndex((el) => el.url === `/${route.params.company}/${route.params.id}`)
  if(idx >= 0) {
    productsStore.setCurrentProductIndex(idx + 1)
  }
}

watch(
    () => productsStore.initialProducts,
    async () => {
      setCurrentIndex()
      for (const image of images.value) {
        if (!imageSizes[image.name]) {
          imageSizes[image.name] = await getImageSize(image)
        }
      }
    },
)

</script>

<template>
  <div class="product-page" itemscope itemtype="http://schema.org/Product">
    <a v-if="!section.isSimpleSection" :id="'product' + section.id"></a>

    <h1 v-if="currentProduct" itemprop="name">{{ currentProduct.title }}</h1>
    <div class="row">
      <div v-if="images && images.length > 0" class="col-lg-4 col-md-4 col-xs-12">
        <div v-if="skinSettings.useFotorama">
          <!--          @TODO implement Fotorama-->
          <!--          <fotorama :images="images" />-->
        </div>
        <div v-else>
          <a v-for="(image, index) in images" :key="image.id"
             :href="getImageUrl(image, '')"
             v-show="localCurrent.index === index"
             :data-size="getImageSizeForImage(image).width + 'x' + getImageSizeForImage(image).height"
             class="img-overlay img-overlay-icon main-image"
             itemprop="associatedMedia"
             itemscope
             itemtype="https://schema.org/ImageObject">
            <img :src="getImageUrl(image, '')" :alt="image.alt" :title="image.title"
                 class="img-responsive img-width-100"/>
            <link :href="getImageUrl(image, '')" itemprop="url"/>
            <meta :content="getImageSizeForImage(image).width" itemprop="width"/>
            <meta :content="getImageSizeForImage(image).height" itemprop="height"/>
          </a>
        </div>
      </div>
      <div class="col-lg-4 col-md-4 col-xs-12">
        <img v-if="images.length === 0" :src="getImageUrl(section?.settings?.section_placeholder || '/assets/img/no_image_big.png', '')"
             class="img-responsive img-width-100"/>
      </div>
      <div class="col-lg-8 col-md-8 col-xs-12">

        <ul v-if="images?.length > 1" class="list-product-images list-small-images list-unstyled"
            v-show="!skinSettings.useFotorama">
          <li v-for="(image, index) in images" :key="image.id">
            <a class="img-overlay"
               @click.prevent="localCurrent.index = index"
               :class="{ active: localCurrent.index === index }"
               href="">
              <img :src="getImageUrl(image, section.imageTypes.product)" :alt="image.alt" :title="image.title"
                   class="img-responsive"/>
            </a>
          </li>
        </ul>
        <accordion>
          <accordion-group v-for="(tab, index) in productsStore.productTabs" :key="index" :index="index">
            <template #heading>
              <img v-if="tab.icon && tab.icon.length" :src="tab.icon[0]" class="product-tab-icon"/>
              {{ tab.title }}
            </template>
            <div v-for="attribute in tab.attributes" :key="attribute.id">
              <!--        {{ attribute.name }}: {{ attribute.value }}-->
              <product-attribute :attribute="attribute" :product="currentProduct"></product-attribute>
            </div>
          </accordion-group>
        </accordion>
      </div>
    </div>
  </div>
<!--  @TODO Contact form?-->
</template>

<style scoped>

</style>